import { LoanDocState, TaskState } from 'gql/graphql';
import { formatMultiple } from '../Shared/utils/documentHelpers';

interface AnnouncementData {
  labelBold: string;
  label: string;
}

export const useInsurance = (task?: any, selectLoanDoc?: any, statusLoanDoc?: string) => {
  const isContact = !!task?.metadata?.email;
  const isObie = task?.metadata?.obieOptionSelected;
  const isDocument = task?.metadata?.documentOptionSelected;
  const isContactedApproved = task?.metadata?.contactedApproved;

  /**
   * Determina qué announcement mostrar según las opciones seleccionadas
   */
  const getAnnouncement = (): AnnouncementData | null => {
    // Solo mostrar announcement si:
    // - El documento no está aprobado
    // - No es un documento existente (no tiene _id)
    // - El task está en Review
    if (
      statusLoanDoc === LoanDocState.Approved ||
      selectLoanDoc?._id ||
      task?.state !== TaskState.Review ||
      selectLoanDoc?.key !== 'insurance'
    ) {
      return null;
    }

    // Múltiples opciones seleccionadas
    if (isContact && isObie) {
      return {
        labelBold: `The borrower has selected multiple insurance options ${formatMultiple({ 
          docs: isDocument, 
          agent: isContact, 
          obie: isObie 
        })}. `,
        label: 'Review them with the borrower and upload the final docs',
      };
    }

    // Solo contact, sin documents
    if (isContact && !isDocument) {
      return {
        labelBold: 'The borrower has given agent contact info. ',
        label: 'Contact the agent and upload the docs here',
      };
    }

    // Solo documents, sin contact
    if (!isContact && isDocument) {
      return {
        labelBold: 'The borrower has uploaded their own docs. ',
        label: 'View and approve them to auto-populate this section',
      };
    }

    return null;
  };

  /**
   * Determina si la expiración es requerida
   * Insurance requiere expiración solo si NO hay contacto con agente
   */
  const isExpirationRequired = (): boolean => {
    return !isContact;
  };

  /**
   * Determina si los documentos son requeridos según el estado
   */
  const isDocumentsRequired = (statusLoanDoc: string | undefined): boolean => {
    return statusLoanDoc === LoanDocState.Approved;
  };

  /**
   * Determina si hay múltiples opciones seleccionadas
   */
  const hasMultipleOptions = (): boolean => {
    return !!(isContact && isObie);
  };

  /**
   * Genera los datos para el InfoBox cuando el estado es MANUAL ACTION
   * Retorna null si no debe mostrarse, o un objeto con la info a mostrar
   */
  const getInfoBoxData = (): { type: 'document' | 'contact' } | null => {
    if (task?.state !== TaskState.Review) {
      return null;
    }

    if (isDocument) {
      return { type: 'document' };
    }

    if (isContact) {
      return { type: 'contact' };
    }

    return null;
  };

  return {
    isContact,
    isObie,
    isDocument,
    isContactedApproved,
    getAnnouncement,
    isExpirationRequired,
    isDocumentsRequired,
    hasMultipleOptions,
    getInfoBoxData,
  };
};
